import React, { useState, useEffect } from 'react';
import { ContentLayout, Button, Grid } from '@cloudscape-design/components';
import { useForm } from 'react-hook-form';
import { Select } from './components/Select';
import { ContactTable } from './ContactTable';
import di from '../../../di/DependencyInjection';
import { GetAICustomersUseCase, GetAICustomersUseCaseName } from '../../../domain/use_cases/ai/GetAICustomersUseCase';
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent';
import { GetAIAgentsUseCase, GetAIAgentsUseCaseName } from '../../../domain/use_cases/ai/GetAIAgentsUseCase';
import { SearchAISummariesByTranscriptionUseCase, SearchAISummariesByTranscriptionUseCaseName } from '../../../domain/use_cases/ai/SearchAISummariesByTranscriptionUseCase';
import { isRight } from 'fp-ts/lib/Either';
import './AiTranscriptionSearchPage.scss';

const AiTranscriptionSearchPage: React.FC = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [customers, setCustomers] = useState<string[]>([]);
  const [agents, setAgents] = useState<string[]>([]);
  const [agentsData, setAgentsData] = useState<any[]>([]);
  const [customer, setCustomer] = useState('');
  const [agent, setAgent] = useState('');
  const [dateInit, setDateInit] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  const [offset, setOffset] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalWithoutClauses, setTotalWithoutClauses] = useState(0);
  const pageSize = 20;


  type SelectOption = { value: string; label: string };

  type ClauseRow = {
    operator: 'AND' | 'OR';
    match_type: 'LIKE' | 'NOT LIKE';
    speaker: 'Agent' | 'Customer';
    phrase: string;
  };
  
  const [clauses, setClauses] = useState<ClauseRow[]>([
    { operator: 'AND', match_type: 'LIKE', speaker: 'Agent', phrase: '' },
  ]);

  const updateClause = (index: number, updatedClause: ClauseRow) => {
    const newClauses = [...clauses];
    newClauses[index] = updatedClause;
    setClauses(newClauses);
  };

  const removeClause = (index: number) => {
    const newClauses = clauses.filter((_, i) => i !== index);
    // Ensure first clause is always 'AND'
    if (newClauses.length > 0) {
      newClauses[0].operator = 'AND';
    }
    setClauses(newClauses);
  };

  const addClause = () => {
    if (clauses.length < 6) {
      setClauses([
        ...clauses,
        { operator: 'AND', speaker: 'Agent', match_type: 'LIKE', phrase: '' }
      ]);
    }
  };

  const isSearchDisabled =
  !customer || !dateInit || !dateEnd || clauses.length === 0 || clauses.some(c => !c.phrase.trim());

  const hasAnyFilter =
  customer !== '' ||
  agent !== '' ||
  dateInit !== '' ||
  dateEnd !== '' ||
  clauses.some(c => c.phrase.trim() !== '');


  const handleReset = () => {
    setCustomer('');
    setAgent('');
    setDateInit('');
    setDateEnd('');
    setClauses([{ operator: 'AND', speaker: 'Agent', match_type: 'LIKE', phrase: '' }]);
    setDateError('');
    setResults([]);
    setOffset(0);
  setTotalRecords(0);
  };

  useEffect(() => {
    const fetchCustomers = async () => {
              const response = await di.get<GetAICustomersUseCase>(GetAICustomersUseCaseName)._aiRepository.getAICustomers('');
      if (isRight(response)) {
        const result = JSON.parse(response.right as string);
        setCustomers(Object.keys(result.data));
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    const fetchAgents = async () => {
      if (customer) {
        const response = await di.get<GetAIAgentsUseCase>(GetAIAgentsUseCaseName)._aiRepository.getAIAgents(customer, '', '');
        if (isRight(response)) {
            const result = JSON.parse(response.right as string);
            setAgentsData(result.data); // ✅ store full agent objects
            const agentNames = result.data.map((user: any) => `${user.firstname}-${user.lastname}`);
            setAgents(agentNames);
          }
      }
    };
    fetchAgents();
  }, [customer]);

  const onSubmit = async () => {
    if (!customer || !dateInit || !dateEnd || !clauses) { alert('Please fill in Customer, Date Range, and Clauses'); return;}
    

    setDateError(null);
    const start = new Date(dateInit);
    console.log("Start", start)
    const end = new Date(dateEnd);
    console.log("End", end)
    if (start > end) {
      console.log("IT happens")
      setDateError('Start date must be before or equal to end date.');
      return;
    }

    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays > 31) {
      setDateError('Date range cannot exceed 31 days.');
      return;
    }

    setIsLoading(true);
    
    try {

        let user_id = '';
        if (agent) {
          const match = agentsData.find((a: any) => `${a.firstname}-${a.lastname}` === agent);
          if (match) user_id = match.user_id;
        }

      const sanitizedClauses = clauses.map(clause => ({
        ...clause,
        phrase: clause.phrase.replace(/'/g, "''")
      }));

    const queryParams = new URLSearchParams({
        dateFrom: dateInit.replace(/-/g, '/'),  // convert YYYY-MM-DD to YYYY/MM/DD
        dateTo: dateEnd.replace(/-/g, '/'),
        customer,
        limit: pageSize.toString(),
        offset: '0',
        clauses: JSON.stringify(sanitizedClauses),
        });

        if (user_id) {
        queryParams.append('user_id', user_id);
        }

      console.log("Query params:", queryParams)
    
      const response 
      = await di.get<SearchAISummariesByTranscriptionUseCase>(SearchAISummariesByTranscriptionUseCaseName)._aiRepository.searchAISummariesByTranscription(queryParams);
      if (isRight(response)) {
        const result = JSON.parse(response.right as string);
        setResults(result.Records);
        if ('TotalRecords' in result) {
          setTotalRecords(result.TotalRecords);
        };
        if ('TotalWithoutClauses' in result) {
          setTotalWithoutClauses(result.TotalWithoutClauses);
        };
        setOffset(result.Records.length);
      }
    } catch (error) {
      console.error('Invalid JSON for clauses', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = async () => {
  setIsLoading(false);
  setIsLoadMore(true);

  let user_id = '';
  if (agent) {
    const match = agentsData.find((a: any) => `${a.firstname}-${a.lastname}` === agent);
    if (match) user_id = match.user_id;
  }

  const sanitizedClauses = clauses.map(clause => ({
    ...clause,
    phrase: clause.phrase.replace(/'/g, "''")
  }));

  const queryParams = new URLSearchParams({
    dateFrom: dateInit.replace(/-/g, '/'),
    dateTo: dateEnd.replace(/-/g, '/'),
    customer,
    limit: pageSize.toString(),
    offset: offset.toString(),
    clauses: JSON.stringify(sanitizedClauses),
  });

  if (user_id) {
    queryParams.append('user_id', user_id);
  }

  try {
    const response = await di
      .get<SearchAISummariesByTranscriptionUseCase>(SearchAISummariesByTranscriptionUseCaseName)
      ._aiRepository.searchAISummariesByTranscription(queryParams);

    if (isRight(response)) {
      const result = JSON.parse(response.right as string);
      setResults(prev => [...prev, ...result.Records]);
      setOffset(prev => prev + result.Records.length);
    }
  } catch (error) {
    console.error('Error loading more:', error);
  } finally {
    setIsLoading(false);
    setIsLoadMore(false);
  }
};

  return (
    <div style={{ padding: '20px' }}>
      <ContentLayout headerBackgroundStyle="transparent">
        <h2>Transcription Search</h2>

        <div className="filter_field_group">
        <p>Customer</p>
        <Select
          options={customers.map(c => ({ label: c, value: c }))}
          value={customer ? { label: customer, value: customer } : null}
          onChange={(e: SelectOption) => setCustomer(e.value)}
        />
        </div>

        <div className="filter_field_group">
        <p>Agent (optional)</p>
        <Select
          options={agents.map(a => ({ label: a, value: a }))}
          value={agent ? { label: agent, value: agent } : null}
          onChange={(e: SelectOption) => setAgent(e.value)}
        />
        </div>

        <div className="filter_field_group">
        <p>Date Init</p>
        <input type="date" className="form-control" value={dateInit} onChange={e => setDateInit(e.target.value)} />
        </div>

        <div className="filter_field_group">
        <p>Date End</p>
        <input type="date" className="form-control" value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
        </div>

        {dateError && (
          <div style={{ color: 'red', marginTop: '8px', fontWeight: 500 }}>
            {dateError}
          </div>
        )}

        <div className="filter_field_group">
            <p>Transcription Clauses</p>

            <div className="clause-builder-wrapper">
            {clauses.map((clause, index) => (
              <div className="clause-row" key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                
                {/* Logical Operator: only editable if not the first clause */}
                <select
                  className="moment_input_creation"
                  value={clause.operator}
                  onChange={(e) =>
                    updateClause(index, {
                      ...clause,
                      operator: e.target.value as ClauseRow['operator'],
                    })
                  }
                  disabled={index === 0}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>

                {/* Speaker Dropdown */}
                <select
                  className="moment_input_creation"
                  value={clause.speaker}
                  onChange={(e) =>
                    updateClause(index, {
                      ...clause,
                      speaker: e.target.value as ClauseRow['speaker'],
                    })
                  }
                >
                  <option value="Agent">Agent</option>
                  <option value="Customer">Customer</option>
                </select>

                {/* Match Type Dropdown */}
                <select
                  className="moment_input_creation"
                  value={clause.match_type}
                  onChange={(e) =>
                    updateClause(index, {
                      ...clause,
                      match_type: e.target.value as ClauseRow['match_type'],
                    })
                  }
                >
                  <option value="LIKE">SAID</option>
                  <option value="NOT LIKE">DIDN'T SAY</option>
                </select>

                {/* Phrase Input */}
                <input
                  type="text"
                  className="moment_input_creation"
                  placeholder="Enter phrase"
                  value={clause.phrase}
                  onChange={(e) =>
                    updateClause(index, {
                      ...clause,
                      phrase: e.target.value,
                    })
                  }
                />

                {/* Remove button (only if more than 1 clause) */}
                {clauses.length > 1 && (
                  <Button
                    onClick={() => removeClause(index)}
                    variant="icon"
                    iconName="close"
                  />
                )}
              </div>
            ))}

              {clauses.length < 6 && (
                <Button onClick={addClause} variant="normal">
                  + Add Clause
                </Button>
              )}
            </div>

            </div>

            <div className="button-container">
            <Button
              variant="primary"
              onClick={() => handleSubmit(onSubmit)()}
              disabled={isSearchDisabled}
            >
              Search
            </Button>

            <Button
              variant="normal"
              onClick={handleReset}
              disabled={!hasAnyFilter}
            >
              Reset
            </Button>
          </div>

        <div style={{ marginTop: '20px' }}>
          {isLoading ? (
            <LoadingComponent />
          ) : (
            <div className="table_home">
              <Grid
                gridDefinition={[
                  { colspan: { default: 12 } },
                  { colspan: { default: 12 } },
                ]}
              >
                <ContactTable data={results as any} />
              </Grid>

              {/* 👇 Spinner only shown while loading more */}
              {isLoadMore && (
                <div style={{ marginTop: '1rem' }}>
                  <LoadingComponent />
                </div>
              )}
            </div>
          )}
        </div>
        
        {totalRecords > 0 && (
          <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '14px', color: '#444' }}>
            Displaying <strong>{results.length}</strong> record{results.length !== 1 ? 's ' : ' '}
            out of <strong>{totalRecords}</strong> matching your search,
            {totalWithoutClauses !== null && (
              <> from a total of <strong>{totalWithoutClauses}</strong> calls in the date range.</>
            )}
          </div>
        )}

        {results.length < totalRecords && (
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <Button onClick={loadMore} loading={isLoading}>
                Load More
              </Button>
            </div>
          )}
      </ContentLayout>
    </div>
  );
};

export default AiTranscriptionSearchPage;
